// PWA Installation Prompt Component

import React from 'react'
import { usePWAInstall } from '../../hooks/usePWA'
import styles from './PWAInstallPrompt.module.css'

interface PWAInstallPromptProps {
  showPrompt: boolean
  onDismiss: () => void
  variant?: 'banner' | 'modal' | 'floating'
}

export const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({
  showPrompt,
  onDismiss,
  variant = 'banner'
}) => {
  const { canInstall, isInstalling, platform, promptInstall } = usePWAInstall()

  if (!showPrompt || !canInstall) {
    return null
  }

  const handleInstall = async () => {
    const success = await promptInstall()
    if (success) {
      onDismiss()
    }
  }

  const getInstallText = () => {
    switch (platform) {
      case 'ios':
        return {
          title: 'Add to Home Screen',
          description: 'Install Clokd for quick access to your event tasks and schedules',
          button: 'Add to Home Screen',
          icon: '📱'
        }
      case 'android':
        return {
          title: 'Install Clokd App',
          description: 'Get the full app experience with offline access and notifications',
          button: 'Install App',
          icon: '📲'
        }
      case 'desktop':
        return {
          title: 'Install Clokd',
          description: 'Install Clokd for faster access and better performance',
          button: 'Install',
          icon: '💻'
        }
      default:
        return {
          title: 'Install App',
          description: 'Install Clokd for the best experience',
          button: 'Install',
          icon: '📱'
        }
    }
  }

  const installText = getInstallText()

  if (variant === 'modal') {
    return (
      <div className={styles.modalOverlay} onClick={onDismiss}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <div className={styles.iconLarge}>{installText.icon}</div>
            <button 
              className={styles.closeButton}
              onClick={onDismiss}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          
          <div className={styles.modalContent}>
            <h2 className={styles.modalTitle}>{installText.title}</h2>
            <p className={styles.modalDescription}>{installText.description}</p>
            
            <div className={styles.benefits}>
              <div className={styles.benefit}>
                <span className={styles.benefitIcon}>⚡</span>
                <span>Faster loading</span>
              </div>
              <div className={styles.benefit}>
                <span className={styles.benefitIcon}>📡</span>
                <span>Works offline</span>
              </div>
              <div className={styles.benefit}>
                <span className={styles.benefitIcon}>🔔</span>
                <span>Push notifications</span>
              </div>
              <div className={styles.benefit}>
                <span className={styles.benefitIcon}>🎯</span>
                <span>Native app feel</span>
              </div>
            </div>
          </div>
          
          <div className={styles.modalActions}>
            <button
              className={styles.cancelButton}
              onClick={onDismiss}
              disabled={isInstalling}
            >
              Maybe Later
            </button>
            <button
              className={styles.installButton}
              onClick={handleInstall}
              disabled={isInstalling}
            >
              {isInstalling ? (
                <>
                  <span className={styles.spinner}></span>
                  Installing...
                </>
              ) : (
                <>
                  <span className={styles.installIcon}>⬇️</span>
                  {installText.button}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (variant === 'floating') {
    return (
      <div className={styles.floatingPrompt}>
        <div className={styles.floatingContent}>
          <div className={styles.floatingIcon}>{installText.icon}</div>
          <div className={styles.floatingText}>
            <div className={styles.floatingTitle}>{installText.title}</div>
            <div className={styles.floatingDesc}>Quick access to your tasks</div>
          </div>
        </div>
        
        <div className={styles.floatingActions}>
          <button
            className={styles.floatingDismiss}
            onClick={onDismiss}
            aria-label="Dismiss"
          >
            ✕
          </button>
          <button
            className={styles.floatingInstall}
            onClick={handleInstall}
            disabled={isInstalling}
          >
            {isInstalling ? '...' : 'Install'}
          </button>
        </div>
      </div>
    )
  }

  // Default banner variant
  return (
    <div className={styles.banner}>
      <div className={styles.bannerContent}>
        <div className={styles.bannerIcon}>{installText.icon}</div>
        <div className={styles.bannerText}>
          <div className={styles.bannerTitle}>{installText.title}</div>
          <div className={styles.bannerDescription}>{installText.description}</div>
        </div>
      </div>
      
      <div className={styles.bannerActions}>
        <button
          className={styles.bannerDismiss}
          onClick={onDismiss}
          disabled={isInstalling}
        >
          ✕
        </button>
        <button
          className={styles.bannerInstall}
          onClick={handleInstall}
          disabled={isInstalling}
        >
          {isInstalling ? 'Installing...' : installText.button}
        </button>
      </div>
    </div>
  )
}